/**
 * A deliberately small Markdown → HTML renderer for page-builder `rich_text`
 * blocks (show descriptions, About sections).
 *
 * ⚠️ PARITY: this is a port of thecomedy's `lib/markdown.ts`. The two repos
 * can't share code, so the pair must be kept in step — a change here needs the
 * same change there or the builder preview and the live page diverge, which is
 * exactly the class of bug this file was written to end. react-markdown is NOT
 * used here any more: it can't render the inline-style syntax below, so the
 * preview would have disagreed with the public page again.
 *
 * WHY THIS EXISTS: the block field is literally called `markdown` and the
 * GigPigs renderer runs it through react-markdown — but this renderer used to
 * emit `esc(markdown).replace(/\n/g,'<br/>')`. So every `**bold**` printed its
 * asterisks literally and every paragraph collapsed into a single line break.
 * Formatting an organiser applied simply did not survive to the public page.
 *
 * SAFETY: input is owner-entered and must be treated as hostile. Everything is
 * HTML-escaped FIRST, then a fixed set of formatting patterns is re-introduced.
 * Raw HTML in the source can never survive, so there is no injection path — the
 * cost is that raw HTML isn't supported, which is the correct trade here.
 *
 * Supported: paragraphs (blank line), line breaks (single newline), # h1-h3,
 * **bold**, *italic*, `code`, [links](url) (http/https/mailto only), bullet and
 * numbered lists, --- rules.
 */

/**
 * Flatten synced HTML (Humanitix / Eventbrite descriptions) into plain text
 * with blank lines between paragraphs, so it can seed a `rich_text` block.
 * rich_text is markdown and never renders raw HTML, so tags left in place would
 * print literally. Lived as a private copy in /s and /t — /event was missing it
 * entirely, which is why HTML descriptions showed their tags there.
 */
export function htmlToParagraphs(html: string | null | undefined): string | null {
  if (!html) return null;
  const text = String(html)
    // Closing a block ends a PARAGRAPH (blank line); <br> is a line break.
    // Collapsing both to a single "\n" was losing the paragraph structure of
    // every synced description.
    .replace(/<\/(p|h[1-6]|div|blockquote)>/gi, '\n\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return text || null;
}

/** True when the string looks like synced HTML rather than typed markdown. */
export function looksLikeHtml(s: string | null | undefined): boolean {
  return !!s && /<\/?(p|br|div|h[1-6]|ul|ol|li|strong|em|span|a)\b/i.test(s);
}

/**
 * Flatten our markdown (+ {{c=;s=;f=|…}} spans) to plain text — for places that
 * need a bare string, not HTML: SEO/OG meta descriptions, previews. Now that
 * bios and descriptions are markdown, feeding them raw into a <meta> tag would
 * leak `**`, `#`, `{{…}}` etc. Strips formatting, keeps the words.
 */
export function stripRichText(src: string | null | undefined): string {
  if (!src) return '';
  return String(src)
    .replace(/\{\{[^|{}]*\|([^{}]*)\}\}/g, '$1')      // {{spec|body}} → body
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')        // [label](url) / ![alt](url) → label
    .replace(/`([^`]+)`/g, '$1')                       // `code` → code
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')                // heading hashes
    .replace(/^\s*>\s?/gm, '')                          // blockquote markers
    .replace(/^\s*[-*+]\s+/gm, '')                      // bullet markers
    .replace(/^\s*\d+[.)]\s+/gm, '')                    // numbered markers
    .replace(/^\s*(?:---|\*\*\*|___)\s*$/gm, '')        // rules
    .replace(/(\*\*|__)(.*?)\1/g, '$2')                // bold
    .replace(/(\*|_)(.*?)\1/g, '$2')                   // italic
    .replace(/\s+/g, ' ')
    .trim();
}

/* ------------------------------------------------------------------ *
 * Inline text styling — colour / font / size on a SELECTION.
 *
 * Written as `{{c=#e13043;s=lg;f=Anton|the styled text}}`. Owners never type
 * that: the Show Description toolbar writes it. Renderers rebuild the <span>
 * from individually VALIDATED parts (hex regex, size from a fixed scale, font
 * name stripped of quotes/brackets), so no attacker-controlled string ever
 * reaches the output as markup — the same escape-first guarantee as the rest
 * of this file. Anything that fails validation renders as plain text.
 *
 * Storing raw HTML instead would have meant sanitising owner HTML server-side,
 * which needs a DOM (jsdom) this app doesn't have.
 * ------------------------------------------------------------------ */

export const TEXT_SIZES: Record<string, string> = {
  xs: '0.75rem', sm: '0.875rem', md: '1rem', lg: '1.25rem', xl: '1.5rem', xxl: '2rem',
};

/** Fonts offered by the editor. Anything else is ignored rather than emitted. */
export const TEXT_FONTS = [
  'Anton', 'Inter', 'Georgia', 'Courier New', 'Impact', 'Times New Roman',
  'Trebuchet MS', 'Verdana', 'Comic Sans MS', 'Bebas Neue', 'Oswald', 'Playfair Display',
];

function hexToRgb(hex: string): [number, number, number] | null {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const n = Number.parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function relLuminance([r, g, b]: [number, number, number]): number {
  const f = (v: number) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/** Surfaces a description can render on: stage black and warm paper. */
export const SURFACE_DARK = '#0C0A09';
export const SURFACE_LIGHT = '#FAF7F0';

/** WCAG contrast ratio between two hex colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return 21;
  const la = relLuminance(ra);
  const lb = relLuminance(rb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Which surfaces a colour will be hard to read on. ADVISORY ONLY — the chosen
 * colour is always rendered exactly as picked.
 *
 * An earlier version clamped the luminance into a "safe" band instead. That
 * silently overrode the owner's choice, which is the wrong trade: this is their
 * page, and a warning informs without taking the decision away.
 */
export function lowContrastOn(hex: string): ('dark' | 'light')[] {
  const out: ('dark' | 'light')[] = [];
  if (contrastRatio(hex, SURFACE_DARK) < 3) out.push('dark');
  if (contrastRatio(hex, SURFACE_LIGHT) < 3) out.push('light');
  return out;
}

/** Build a style attribute from validated parts only. */
function inlineStyle(spec: string): string {
  const parts: string[] = [];
  for (const pair of spec.split(';')) {
    const [rawK, rawV] = pair.split('=');
    const k = (rawK ?? '').trim();
    const v = (rawV ?? '').trim();
    if (!k || !v) continue;
    if (k === 'c' && /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(v)) {
      // Exactly as picked. The editor warns about low contrast; it does not
      // override — see lowContrastOn().
      parts.push(`color:${v}`);
    } else if (k === 's') {
      // Two accepted forms: a legacy size KEY (xs…xxl, still written by older
      // content and the block-level size control) or an explicit pixel value
      // `s=24px` (what the rich_text WYSIWYG now writes — organisers asked for
      // real px sizing). Both are validated before emitting.
      if (TEXT_SIZES[v]) {
        parts.push(`font-size:${TEXT_SIZES[v]}`);
      } else if (/^\d{1,3}px$/.test(v)) {
        const px = Number.parseInt(v, 10);
        if (px >= 8 && px <= 300) parts.push(`font-size:${px}px`);
      }
    } else if (k === 'f') {
      const font = TEXT_FONTS.find((f) => f.toLowerCase() === v.toLowerCase());
      if (font) parts.push(`font-family:'${font}',sans-serif`);
    }
  }
  return parts.join(';');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

/** Only http/https/mailto — blocks javascript: and data: URLs. */
function safeUrl(url: string): string | null {
  const u = url.trim();
  return /^(https?:\/\/|mailto:|\/)/i.test(u) ? u : null;
}

/** Inline formatting, applied to ALREADY-ESCAPED text. */
function inline(escaped: string): string {
  let out = escaped;
  // Styled spans first so emphasis inside them still applies. The braces are
  // matched AFTER escaping, so this can only ever see literal text.
  //
  // Resolved INNERMOST-FIRST, repeatedly. The body class `[^{}]` cannot contain
  // braces, so with a NESTED span only the inner one ever matched and the outer
  // `{{spec|` + `}}` printed as literal text on the page. Nesting is not exotic:
  // it is exactly what the WYSIWYG serialises when you colour part of a run that
  // already carries a size or font (serializeInline wraps each styled element
  // independently), which is how "{{c=#ada81a|UP FOR GRABS:}}" ended up visible
  // on a live series page. Each pass replaces the innermost spans with <span>
  // HTML, which contains no braces, so the next pass sees the one outside it.
  // Bounded so a malformed `{{` can never spin.
  for (let pass = 0; pass < 6; pass += 1) {
    const next = out.replace(/\{\{([^|{}]*)\|([^{}]*)\}\}/g, (_m, spec: string, body: string) => {
      const style = inlineStyle(spec);
      return style ? `<span style="${style}">${body}</span>` : body;
    });
    if (next === out) break;
    out = next;
  }
  // Links — their label may itself contain emphasis.
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label: string, href: string) => {
    const url = safeUrl(href);
    if (!url) return label;
    const external = /^https?:\/\//i.test(url);
    return `<a href="${url}"${external ? ' target="_blank" rel="noopener noreferrer"' : ''}>${label}</a>`;
  });
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  // Bold before italic so **x** isn't eaten by the single-asterisk rule.
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/(^|[^_])_([^_\n]+)_/g, '$1<em>$2</em>');
  return out;
}

export function markdownToHtml(src: string | null | undefined): string {
  if (!src) return '';
  const text = escapeHtml(String(src).replace(/\r\n?/g, '\n')).trim();
  if (!text) return '';

  const out: string[] = [];
  // Blank line = paragraph boundary, which is what makes a pasted description
  // read as paragraphs rather than one run-on block.
  for (const rawBlock of text.split(/\n{2,}/)) {
    const block = rawBlock.trim();
    if (!block) continue;

    if (/^(---|\*\*\*|___)$/.test(block)) { out.push('<hr/>'); continue; }

    const heading = block.match(/^(#{1,3})\s+(.*)$/);
    if (heading && !block.includes('\n')) {
      const level = heading[1].length + 1; // # -> h2, page owns h1
      out.push(`<h${Math.min(level, 4)}>${inline(heading[2].trim())}</h${Math.min(level, 4)}>`);
      continue;
    }

    const lines = block.split('\n');
    const bulleted = lines.every((l) => /^\s*[-*+]\s+/.test(l));
    if (bulleted) {
      out.push(`<ul>${lines.map((l) => `<li>${inline(l.replace(/^\s*[-*+]\s+/, ''))}</li>`).join('')}</ul>`);
      continue;
    }
    // Blockquote — every line starts with `>` (already escaped to `&gt;`).
    // The WYSIWYG's Quote button serialises to this; without it the quote
    // printed a literal "> " on the public page.
    // ⚠️ Mirrored in the other repo's renderer — change both.
    const quoted = lines.every((l) => /^\s*&gt;/.test(l));
    if (quoted) {
      const body = lines.map((l) => l.replace(/^\s*&gt;\s?/, '').trim()).join('\n');
      out.push(`<blockquote>${inline(body).replace(/\n/g, '<br/>')}</blockquote>`);
      continue;
    }
    const numbered = lines.every((l) => /^\s*\d+[.)]\s+/.test(l));
    if (numbered) {
      out.push(`<ol>${lines.map((l) => `<li>${inline(l.replace(/^\s*\d+[.)]\s+/, ''))}</li>`).join('')}</ol>`);
      continue;
    }

    // Single newlines inside a paragraph become line breaks — organisers use
    // them for addresses, times and lineup lists. Run inline() on the WHOLE
    // paragraph (not per line) so bold / italic / {{…}} styling that spans
    // several lines still parses: a multi-line `**{{s=18px|…}}**` was printing
    // its literal asterisks/braces because inline() only ever saw one line and
    // never met the closing marker. Convert the surviving \n to <br/> after.
    const para = lines.map((l) => l.trim()).join('\n');
    out.push(`<p>${inline(para).replace(/\n/g, '<br/>')}</p>`);
  }

  return out.join('');
}
